import { useEffect, useState, useCallback, useRef } from 'react';
import '@/App.css';
import '@/i18n';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from '@/components/ui/sonner';

import useLenis from '@/hooks/useLenis';
import Loader from '@/components/Loader';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import About from '@/components/About';
import Marquee from '@/components/Marquee';
import Products from '@/components/Products';
import Cases from '@/components/Cases';
import Tips from '@/components/Tips';
import FAQ from '@/components/FAQ';
import Contact from '@/components/Contact';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import FloatingContact from '@/components/FloatingContact';
import { BrandProvider, useBrand } from '@/context/BrandContext';

function AppShell() {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const { activeBrand } = useBrand();
  const prevBrandRef = useRef(null);
  useLenis(!loading);

  // Handle scroll targets when switching between brand page and home.
  useEffect(() => {
    const prev = prevBrandRef.current;
    const scrollToProducts = () => {
      const el = document.getElementById('products');
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, left: 0, behavior: 'auto' });
      }
    };

    if (activeBrand && !prev) {
      // Entering a brand page → jump to top.
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } else if (!activeBrand && prev) {
      // Returning to home → land on the brands grid.
      // Give React/AnimatePresence a beat to mount the home layout first.
      const t1 = setTimeout(scrollToProducts, 60);
      const t2 = setTimeout(scrollToProducts, 500);
      prevBrandRef.current = activeBrand;
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    prevBrandRef.current = activeBrand;
  }, [activeBrand]);

  useEffect(() => {
    const applyLang = (lng) => {
      const dir = lng === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.setAttribute('lang', lng);
      document.documentElement.setAttribute('dir', dir);
      document.body.style.fontFamily =
        lng === 'ar'
          ? "'Thmanyah', 'Tajawal', 'IBM Plex Arabic', system-ui, sans-serif"
          : "'Manrope', system-ui, sans-serif";
    };
    applyLang(i18n.language || 'ar');
    i18n.on('languageChanged', applyLang);
    return () => i18n.off('languageChanged', applyLang);
  }, [i18n]);

  useEffect(() => {
    document.title = i18n.language === 'ar'
      ? 'دواكم — ثقة بلا حدود'
      : 'Dowakum — Trust Without Limits';
  }, [i18n.language]);

  const toggleLang = useCallback(() => {
    const next = i18n.language === 'ar' ? 'en' : 'ar';
    const y = window.scrollY;
    i18n.changeLanguage(next);
    requestAnimationFrame(() => window.scrollTo({ top: y, left: 0, behavior: 'auto' }));
  }, [i18n]);

  const isBrandPage = Boolean(activeBrand);

  return (
    <div className="dwk-app" data-testid="app-root">
      <AnimatePresence>
        {loading && <Loader onDone={() => setLoading(false)} />}
      </AnimatePresence>

      <CustomCursor />
      <Navbar onLangToggle={toggleLang} />

      <AnimatePresence mode="wait">
        <motion.main
          key={`${i18n.language}-${isBrandPage ? 'brand' : 'home'}`}
          initial={{ opacity: 0, filter: 'blur(6px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, filter: 'blur(6px)' }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          {isBrandPage ? (
            <>
              <Products />
              <Footer />
            </>
          ) : (
            <>
              <Hero />
              <Marquee />
              <About />
              <Products />
              <Cases />
              <Tips />
              <FAQ />
              <Contact />
              <Footer />
            </>
          )}
        </motion.main>
      </AnimatePresence>

      <FloatingContact />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default function App() {
  return (
    <BrandProvider>
      <AppShell />
    </BrandProvider>
  );
}
